import React, { useState, useEffect } from "react";
import {
  getClinics,
  getWorkSchedules,
  createWorkSchedules,
  updateWorkSchedule,
  deleteWorkSchedule,
} from "../../api";

const WorkSchedulePage = () => {
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" или "edit"
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // Форма для создания/редактирования
  const [formData, setFormData] = useState({
    dates: [],
    administrators: [{ first_name: "", last_name: "" }], // Массив администраторов
  });

  // Загрузка клиник при монтировании
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const clinicsData = await getClinics();
        setClinics(clinicsData);
      } catch (error) {
        setStatusMessage(`Ошибка при загрузке клиник: ${error.message}`);
      }
    };
    fetchClinics();
  }, []);

  // Загрузка графиков при выборе клиники или смене месяца
  useEffect(() => {
    if (selectedClinic) {
      loadSchedules();
    }
  }, [selectedClinic, currentMonth]);

  const loadSchedules = async () => {
    setIsLoading(true);
    try {
      const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const dateFrom = formatDateISO(firstDay);
      const dateTo = formatDateISO(lastDay);

      const data = await getWorkSchedules(selectedClinic.client_id, dateFrom, dateTo);
      setSchedules(data.schedules || []);
      setStatusMessage("");
    } catch (error) {
      setStatusMessage(`Ошибка при загрузке графиков: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };



  const formatDateISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (dateString) => {
    const [year, month, day] = dateString.split("-");
    return `${day}.${month}.${year}`;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const getMonthName = () => {
    return currentMonth.toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
  };

  const getDaysInMonth = () => {
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const days = [];

    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }

    return days;
  };

  const getScheduleForDate = (date) => {
    const dateStr = formatDateISO(date);
    return schedules.filter((s) => s.date.startsWith(dateStr));
  };

  const handleOpenCreateModal = (date = null) => {
    setModalMode("create");
    setSelectedSchedule(null);
    setFormData({
      dates: date ? [formatDateISO(date)] : [],
      administrators: [{ first_name: "", last_name: "" }],
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (schedule) => {
    setModalMode("edit");
    setSelectedSchedule(schedule);
    setFormData({
      dates: [],
      administrators: [{ first_name: schedule.first_name, last_name: schedule.last_name }],
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSchedule(null);
    setFormData({
      dates: [],
      administrators: [{ first_name: "", last_name: "" }],
    });
  };


  const handleDateToggle = (date) => {
    const dateStr = formatDateISO(date);
    const newDates = formData.dates.includes(dateStr)
      ? formData.dates.filter((d) => d !== dateStr)
      : [...formData.dates, dateStr];

    setFormData({ ...formData, dates: newDates });
  };

  const handleAddAdministrator = () => {
    setFormData({
      ...formData,
      administrators: [...formData.administrators, { first_name: "", last_name: "" }],
    });
  };

  const handleRemoveAdministrator = (index) => {
    const newAdmins = formData.administrators.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      administrators: newAdmins.length > 0 ? newAdmins : [{ first_name: "", last_name: "" }],
    });
  };

  const handleAdministratorChange = (index, field, value) => {
    const newAdmins = [...formData.administrators];
    newAdmins[index][field] = value;
    setFormData({ ...formData, administrators: newAdmins });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (modalMode === "create") {
        // Создаём записи для каждого администратора
        for (const admin of formData.administrators) {
          if (admin.first_name && admin.last_name) {
            await createWorkSchedules({
              clinic_id: selectedClinic.client_id,
              dates: formData.dates,
              first_name: admin.first_name,
              last_name: admin.last_name,
            });
          }
        }
        setStatusMessage("График успешно создан");
      } else {
        // Редактирование - обновляем только одну запись
        const admin = formData.administrators[0];
        await updateWorkSchedule(selectedSchedule._id, {
          first_name: admin.first_name,
          last_name: admin.last_name,
        });
        setStatusMessage("График успешно обновлён");
      }

      handleCloseModal();
      loadSchedules();
    } catch (error) {
      setStatusMessage(`Ошибка: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm("Вы уверены, что хотите удалить эту запись графика?")) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteWorkSchedule(scheduleId);
      setStatusMessage("График успешно удалён");
      loadSchedules();
    } catch (error) {
      setStatusMessage(`Ошибка при удалении: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const days = getDaysInMonth();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">
            График работы <span className="gradient-text">администраторов</span>
          </h1>
          <p className="text-gray-600 text-xl">
            Управление расписанием для определения администратора через AI
          </p>
        </div>

        {/* Выбор клиники */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Выберите клинику
          </label>
          <select
            value={selectedClinic?.client_id || ""}
            onChange={(e) => {
              const clinic = clinics.find((c) => c.client_id === e.target.value);
              setSelectedClinic(clinic);
            }}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value="">-- Выберите клинику --</option>
            {clinics.map((clinic) => (
              <option key={clinic.client_id} value={clinic.client_id}>
                {clinic.name}
              </option>
            ))}
          </select>
        </div>

        {selectedClinic && (
          <>
            {/* Навигация по месяцам */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={handlePrevMonth}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  ← Назад
                </button>
                <h2 className="text-2xl font-bold capitalize">{getMonthName()}</h2>
                <button
                  onClick={handleNextMonth}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Вперёд →
                </button>
              </div>
            </div>

            {/* Таблица графика */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Дата
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Администратор
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Действия
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {days.map((day) => {
                      const daySchedules = getScheduleForDate(day);
                      const hasSchedule = daySchedules.length > 0;
                      const rowClass = hasSchedule ? "bg-green-50" : "bg-red-50";

                      return (
                        <tr key={formatDateISO(day)} className={rowClass}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatDateDisplay(formatDateISO(day))}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {hasSchedule ? (
                              <div>
                                {daySchedules.map((schedule, idx) => (
                                  <div key={schedule._id} className="mb-1">
                                    {schedule.first_name} {schedule.last_name}
                                    {idx < daySchedules.length - 1 && ", "}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-red-600 font-semibold">⚠️ Не заполнено</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {hasSchedule ? (
                              <div className="flex space-x-2">
                                {daySchedules.map((schedule) => (
                                  <React.Fragment key={schedule._id}>
                                    <button
                                      onClick={() => handleOpenEditModal(schedule)}
                                      className="text-blue-600 hover:text-blue-900"
                                      title="Редактировать"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      onClick={() => handleDelete(schedule._id)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Удалить"
                                    >
                                      🗑️
                                    </button>
                                  </React.Fragment>
                                ))}
                              </div>
                            ) : (
                              <button
                                onClick={() => handleOpenCreateModal(day)}
                                className="text-green-600 hover:text-green-900"
                                title="Добавить"
                              >
                                ➕
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Кнопка массового добавления */}
            <div className="mt-6">
              <button
                onClick={() => handleOpenCreateModal()}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-md hover:from-blue-700 hover:to-purple-700 font-medium"
              >
                ➕ Добавить записи графика
              </button>
            </div>
          </>
        )}

        {/* Статусное сообщение */}
        {statusMessage && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-800">{statusMessage}</p>
          </div>
        )}

        {/* Модальное окно */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">
                {modalMode === "create" ? "Добавить записи графика" : "Редактировать запись"}
              </h2>

              <form onSubmit={handleSubmit}>
                {modalMode === "create" && (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Выберите даты *
                      </label>
                      <div className="grid grid-cols-7 gap-2 max-h-64 overflow-y-auto border p-2 rounded-md">
                        {days.map((day) => {
                          const dateStr = formatDateISO(day);
                          const isSelected = formData.dates.includes(dateStr);

                          return (
                            <button
                              key={dateStr}
                              type="button"
                              onClick={() => handleDateToggle(day)}
                              className={`p-2 rounded-md text-sm ${
                                isSelected
                                  ? "bg-blue-600 text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {day.getDate()}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Выбрано дат: {formData.dates.length}
                      </p>
                    </div>
                  </>
                )}

                {/* Список администраторов */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Администраторы *
                    </label>
                    {modalMode === "create" && (
                      <button
                        type="button"
                        onClick={handleAddAdministrator}
                        className="text-sm bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700"
                      >
                        ➕ Добавить администратора
                      </button>
                    )}
                  </div>

                  {formData.administrators.map((admin, index) => (
                    <div key={index} className="mb-3 p-3 border border-gray-300 rounded-md bg-gray-50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Администратор {index + 1}
                        </span>
                        {modalMode === "create" && formData.administrators.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAdministrator(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            ✕ Удалить
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Имя *</label>
                          <input
                            type="text"
                            value={admin.first_name}
                            onChange={(e) => handleAdministratorChange(index, "first_name", e.target.value)}
                            required
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="Анастасия"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Фамилия *</label>
                          <input
                            type="text"
                            value={admin.last_name}
                            onChange={(e) => handleAdministratorChange(index, "last_name", e.target.value)}
                            required
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="Иванова"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {isLoading ? "Сохранение..." : "Сохранить"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkSchedulePage;
